import { map, Observable, throwError } from 'rxjs'
import { ajax, AjaxResponse } from 'rxjs/ajax'
import { catchError } from 'rxjs/operators'
import { env } from 'shared/env'

import { post } from './api-routes.common'
import { IVendorUpdateData } from '../../decl'

const baseURL = env.SERVER_ADDR

interface RequestHeaders {
  'Content-Type': string
  Authorization?: string
}

// Vendor-related interfaces
interface IVendorBalance {
  vendor_id: string
  balance: number
  currency: string
  last_updated: string
}

interface IVendor {
  id: string
  name: string
  contact_person: string
  phone: string
  email?: string
  address: string
  city: string
  business_type: string
  contract_start_date: string
  term_id: string
  status: 'active' | 'inactive' | 'pending'
  created_at: string
  updated_at: string
}

interface IContractType {
  id: string
  name: string
  description?: string
  terms: string
  duration_months: number
}

interface IVendorPayment {
  id: string
  vendor_id: string
  amount: number
  currency: string
  payment_date: string
  status: 'pending' | 'completed' | 'failed'
  reference: string
}

interface IBusinessType {
  id: string
  name: string
  description?: string
  category: string
}

interface IVendorCommission {
  vendor_id: string
  commission_rate: number
  commission_type: 'percentage' | 'fixed'
  start_date: string
  end_date?: string
  status: 'active' | 'inactive'
}

interface IVendorBalancesResponse {
  vendors: IVendorBalance[]
  total_balance: number
  currency: string
}

const requestHeader = (): RequestHeaders => {
  const headers: RequestHeaders = {
    'Content-Type': 'application/json',
  }
  const userData = JSON.parse(localStorage.getItem('user_info') || '{}')
  let token: string | boolean = false
  if (userData.token) {
    token = userData.token
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

interface ErrorResponse {
  status: number
  message: string
}

const handleError = (operation: string) => (err: ErrorResponse) => {
  const errMsg = `Error in ${operation}()`

  if (err.status === 401) {
    localStorage.clear()
    window.location.href = '/auth/login'
  }
  return throwError(() => console.error(errMsg))
}

export const getVendorsBalance = (): Observable<
  AjaxResponse<IVendorBalance[]>
> =>
  ajax<IVendorBalance[]>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/vendors/dashboard`,
  }).pipe(catchError(handleError('login')))

export const getVendors = (): Observable<AjaxResponse<IVendor[]>> =>
  ajax<IVendor[]>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/vendors`,
  }).pipe(catchError(handleError('login')))

export const getContractTypes = (): Observable<AjaxResponse<IContractType[]>> =>
  ajax<IContractType[]>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/vendors/contracts/types`,
  }).pipe(catchError(handleError('login')))

export const getVendorsCalculatedPayment = (
  vendoId: string,
): Observable<AjaxResponse<IVendorPayment[]>> =>
  ajax<IVendorPayment[]>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/vendors/${vendoId}/payments`,
  }).pipe(catchError(handleError('login')))

export const getBusinessTypes = (): Observable<AjaxResponse<IBusinessType[]>> =>
  ajax<IBusinessType[]>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/settings/business-party-types`,
  }).pipe(catchError(handleError('login')))

export const updateVendors = (
  data: IVendorUpdateData,
  vendoId: string,
): Observable<AjaxResponse<IVendor>> =>
  ajax<IVendor>({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/vendors/${vendoId}`,
    body: data,
  }).pipe(catchError(handleError('login')))

export const processVendorCommission = (
  data: IVendorCommission,
  vendoId: string,
): Observable<AjaxResponse<IVendorCommission>> =>
  ajax<IVendorCommission>({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/vendors/${vendoId}/commissions`,
    body: data,
  }).pipe(catchError(handleError('login')))

export const getVendorBalances = (): Observable<IVendorBalancesResponse> => {
  return ajax
    .get<IVendorBalancesResponse>('/api/vendors/balances')
    .pipe(
      map(
        (response: AjaxResponse<IVendorBalancesResponse>) => response.response,
      ),
    )
}

export const updateVendorInfo = (
  data: IVendorUpdateData,
): Observable<IVendorUpdateData> => {
  return post('/api/vendors/update', data)
}
